import type { PropsWithChildren, ReactElement } from 'react';
import { useEffect, useMemo } from 'react';
import '@fastnorth/kraken/ThemeProvider.css';
import { useApp } from '../hooks/AppProvider';
import { useSession } from '../hooks/SessionProvider';
import { useLocation } from 'react-router-dom';
import deriveTitleFromPath from './utils/deriveTitleFromPath';
import {
  AppLayout,
  AppLayoutSkeleton,
  type PageBannerProps,
  type PageFooterProps,
  type PageHeaderProps,
  type LayoutSlots,
} from '@fastnorth/kraken/components/page-layout';
import { useTranslation } from 'react-i18next';
import type { PageContextProps } from '@fastnorth/kraken/components/page-context';
import ParticipantSearch from '../components/participant-search/ParticipantSearch';
import type { ResultEvent } from '../types/events';
import useNavigationLinks from '../hooks/useNavigationLinks.ts';
import EventLink from './components/EventLink';
import handleUserSession from './utils/handleUserSession';
import useUserLinks from './utils/useUserLinks';
import setUserLocale from '../i18n/utils/setUserLocale';
import type { Locale } from '@fastnorth/i18next';
import TimingMenuGroup from './components/TimingMenuGroup';
import { Flex, Div } from '@fastnorth/kraken/components/layout';
import ShareButton from './components/ShareButton';
import useAppLocation from '../hooks/useAppLocation.ts';
import type { LayoutPropsTuple } from './types';
import { createEventDateFormatter } from './utils/createEventDateFormatter';
import isMidnightInTimeZone from './utils/isMidnightInTimeZone.ts';
import isPWA from '../utils/isPWA.ts';
import EventSearchLink from './components/EventSearchLink.tsx';
import ContactTimerSheet from '../components/form/contact-timer/components/ContactTimerSheet.tsx';

const Layout = ({ children }: PropsWithChildren): ReactElement => {
  const { i18n, t } = useTranslation('translation', { keyPrefix: 'karwa' });
  const [{ event, brandingColors }] = useApp();
  const [{ session, clientUuid }] = useSession();
  const locationState = useAppLocation();
  const { isProgress, isLeaderboard } = locationState;
  const { pathname } = useLocation();
  const {
    locale,
    location: eventLocation,
    styles,
    eventUrl,
  } = (event || {}) as unknown as ResultEvent;
  const { user } = session || {};
  const pwa = isPWA();

  const navLinks = useNavigationLinks();
  const userLinks = useUserLinks();

  const eventTimestamp = event?.dates?.eventDate ? new Date(event.dates.eventDate).getTime() : NaN;
  const isValidDate = !isNaN(eventTimestamp);

  const dateFormatter = useMemo(() => {
    const timezone = eventLocation?.timezone || 'UTC';
    const userLocale = i18n.language || 'en-US';
    // Events without a start time are stored as midnight, so the time and timezone are omitted
    const includeTime =
      !isNaN(eventTimestamp) && !isMidnightInTimeZone(new Date(eventTimestamp), timezone);

    return createEventDateFormatter(timezone, userLocale, includeTime);
  }, [eventLocation, i18n.language, eventTimestamp]);

  const menuGroups = [
    <TimingMenuGroup key={'timing-menu-group'} />,
    <EventLink key={'event-link'} eventUrl={eventUrl || ''} />,
  ];

  if (pwa) {
    menuGroups.push(<EventSearchLink key={'event-search'} />);
  }

  const { available: availableLocales = [] } = locale || {};

  useEffect(() => {
    if (event) {
      document.title = deriveTitleFromPath(pathname, event.name);
    }
  }, [pathname, event]);

  const [slots, bannerProps] = useMemo<LayoutPropsTuple>(() => {
    const slotProps: LayoutSlots = {};
    if (!isProgress) {
      /**
       * Priority: Dashboard branding banner overrides event styles
       * desktopBannerImageUrl is set to desktop branding image if it exists, otherwise falls back to event's background image
       * mobileBannerImageUrl is set to mobile branding image if it exists, otherwise falls back to desktopBannerImageUrl
       * (which could be either the desktop branding image or the event background image)
       */
      const eventBackgroundImageUrl = styles?.backgroundImage;
      const desktopBrandingBannerImageUrl = event?.branding?.banner?.[0]?.imageUrl;
      const mobileBrandingBannerImageUrl = event?.branding?.banner?.[1]?.imageUrl;
      const desktopBannerImageUrl = desktopBrandingBannerImageUrl || eventBackgroundImageUrl;
      const mobileBannerImageUrl = mobileBrandingBannerImageUrl || desktopBannerImageUrl;

      const targetUrl = event?.branding?.banner?.[0]?.targetUrl;
      const targetUrlLabel = event?.branding?.banner?.[0]?.targetUrlLabel;

      // Only create bannerProps if there's a valid banner image
      const bannerProps: PageBannerProps | undefined =
        mobileBannerImageUrl || desktopBannerImageUrl
          ? {
              bannerImage: {
                mobile: mobileBannerImageUrl,
                desktop: desktopBannerImageUrl,
              },
              ...(targetUrl && targetUrlLabel
                ? {
                    target: {
                      url: targetUrl,
                      label: `${targetUrlLabel} - ${t('legal.custom_link_warning')}`,
                    },
                  }
                : {}),
            }
          : undefined;

      if (!isProgress) {
        slotProps.callToAction = (
          <Flex gap={'6'}>
            <Div className={'hidden lg:!block'}>
              <ShareButton trackingLocation={'hamburger_menu'} />
            </Div>
            <ParticipantSearch />
          </Flex>
        );
      }
      return [slotProps, bannerProps];
    }

    return [slotProps, undefined];
  }, [isProgress, styles, event, t]);

  if (!event) {
    return <AppLayoutSkeleton />;
  }

  const logo = styles?.logo
    ? {
        src: styles.logo,
        alt: event!.name,
        href: '/',
      }
    : undefined;

  const pageHeader: PageHeaderProps = {
    hamburgerMenu: {
      navLinks,
      menuGroups: menuGroups,
      title: t('layout.header.main_menu'),
      languageSelector: {
        availableLocales,
        onValueChange: async (value: string) => await setUserLocale(value as Locale),
        selectedValue: i18n.language,
      },
      userContext: {
        user: user ?? undefined,
        links: userLinks,
        sessionActions: {
          signOut: {
            onClick: () => handleUserSession(user),
            label: t('layout.header.sign_out'),
          },
          signIn: {
            onClick: () => handleUserSession(user),
            label: t('layout.header.sign_in'),
          },
        },
      },
    },
  };

  const containerFooterSlot = (
    <Flex className={'lg:w-fit lg:ml-auto'}>
      <ContactTimerSheet />
    </Flex>
  );

  const pageFooter: PageFooterProps = {
    copyRightLabel: t('layout.footer.copyright_label', { year: new Date().getFullYear() }),
    locale: i18n.language as Locale,
    privacyLinkLabel: t('layout.footer.privacy_link_label'),
    termsLinkLabel: t('layout.footer.terms_link_label'),
    isQuebecEvent: eventLocation?.address?.administrativeArea === 'QC',
    byRaceRoster: t('layout.footer.by_race_roster'),
    additionalInfo: clientUuid ? `# ${clientUuid}` : undefined,
  };

  const brandedBackground = event?.branding?.background?.[0]
    ? { srcUrl: event?.branding?.background?.[0].imageUrl }
    : undefined;

  const locationString = [
    event!.location?.address?.locality,
    event!.location?.address?.administrativeArea,
    event!.location?.address?.countryCode,
  ]
    .filter((field) => field !== undefined && field !== null)
    .join(', ');

  const pageCtx: PageContextProps = {
    name: event!.name,
    dateConfig: isValidDate
      ? {
          dates: [new Date(eventTimestamp)],
          format: dateFormatter,
        }
      : undefined,
    location: locationString,
    logo,
  };

  return (
    <AppLayout
      brandingColors={brandingColors}
      pageHeader={pageHeader}
      pageNavLinks={navLinks}
      pageFooter={pageFooter}
      bannerProps={bannerProps}
      brandedBackground={brandedBackground}
      pageCtx={pageCtx}
      dynamicWidth={isLeaderboard}
      slots={slots}
      containerFooterSlot={!isProgress ? containerFooterSlot : undefined}>
      {children}
    </AppLayout>
  );
};

export default Layout;
